import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Trash2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MediaUploader from "@/components/admin/MediaUploader";
import MediaThumb from "@/components/admin/MediaThumb";
import { api, formatApiError } from "@/lib/api";

export const PROJECT_CATEGORIES = ["Residential", "Commercial", "Industrial", "CCTV / Security"];
const EMPTY = { title: "", category: "Residential", service: "", summary: "", media: [], published: true };

// Create / edit dialog for an Our Work project. `project` null = create.
export default function ProjectEditor({ open, project, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(project ? { title: project.title, category: project.category, service: project.service || "", summary: project.summary || "", media: project.media || [], published: project.published } : EMPTY);
  }, [open, project]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const move = (i, dir) => {
    const m = [...form.media];
    const j = i + dir;
    if (j < 0 || j >= m.length) return;
    [m[i], m[j]] = [m[j], m[i]];
    set("media", m);
  };

  const save = async () => {
    if (form.title.trim().length < 2) return toast.error("Give the project a title");
    if (form.media.length === 0) return toast.error("Add at least one real photo or video");
    setSaving(true);
    try {
      const payload = { ...form, title: form.title.trim(), service: form.service.trim() || null, summary: form.summary.trim() || null };
      const { data } = project ? await api.patch(`/admin/projects/${project.id}`, payload) : await api.post("/admin/projects", payload);
      toast.success(project ? "Project updated" : "Project added");
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-[#26262f] bg-[#0f0f14] text-white sm:max-w-2xl" data-testid="project-editor">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase">{project ? "Edit project" : "Add project"}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs uppercase tracking-widest text-neutral-500">Title</label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. 200A panel upgrade" className="border-[#2a2a34] bg-[#0f0f15] text-white" data-testid="project-title-input" />
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-widest text-neutral-500">Category</label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger className="border-[#2a2a34] bg-[#0f0f15]" data-testid="project-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PROJECT_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-3 pb-2">
            <Switch checked={form.published} onCheckedChange={(v) => set("published", v)} data-testid="project-published-switch" />
            <span className="text-sm text-neutral-300">{form.published ? "Published on Our Work" : "Hidden (draft)"}</span>
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs uppercase tracking-widest text-neutral-500">Service (optional)</label>
            <Input value={form.service} onChange={(e) => set("service", e.target.value)} placeholder="e.g. Panel Upgrades" className="border-[#2a2a34] bg-[#0f0f15] text-white" data-testid="project-service-input" />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs uppercase tracking-widest text-neutral-500">Summary (optional)</label>
            <Textarea value={form.summary} onChange={(e) => set("summary", e.target.value)} rows={3} maxLength={600} placeholder="Short description of the job" className="border-[#2a2a34] bg-[#0f0f15] text-white" data-testid="project-summary-input" />
          </div>
        </div>

        <div>
          <p className="mb-2 text-xs uppercase tracking-widest text-neutral-500">Media ({form.media.length}) — first item is the cover</p>
          {form.media.length > 0 && (
            <ul className="mb-3 space-y-2" data-testid="project-media-list">
              {form.media.map((m, i) => (
                <li key={m.file_id || m.url} className="flex items-center gap-3 rounded-lg border border-[#20202a] bg-[#101017] p-2">
                  <MediaThumb item={m} className="h-16 w-20" />
                  <div className="min-w-0 flex-1 text-xs">
                    <p className="truncate text-neutral-200">{m.filename || m.url.split("/").pop()}</p>
                    <p className="uppercase text-neutral-500">{m.type}{i === 0 ? " · cover" : ""}</p>
                  </div>
                  <div className="flex gap-1">
                    <IconBtn onClick={() => move(i, -1)} disabled={i === 0} label="Move up" testId={`media-up-${i}`}><ArrowUp className="h-4 w-4" /></IconBtn>
                    <IconBtn onClick={() => move(i, 1)} disabled={i === form.media.length - 1} label="Move down" testId={`media-down-${i}`}><ArrowDown className="h-4 w-4" /></IconBtn>
                    <IconBtn onClick={() => set("media", form.media.filter((_, k) => k !== i))} label="Remove" danger testId={`media-remove-${i}`}><Trash2 className="h-4 w-4" /></IconBtn>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <MediaUploader onUploaded={(r) => setForm((f) => ({ ...f, media: [...f.media, { type: r.type, url: r.url, file_id: r.id, filename: r.filename }] }))} testId="project-uploader" />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-[#2a2a34] px-4 py-2 text-sm font-semibold text-white hover:border-neutral-500" data-testid="project-cancel">Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-amber-400 px-5 py-2 text-sm font-bold text-[#0A0A0C] hover:bg-amber-300 disabled:opacity-60" data-testid="project-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {project ? "Save changes" : "Add project"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function IconBtn({ onClick, disabled, label, danger, testId, children }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={label} aria-label={label} data-testid={testId}
      className={`flex h-8 w-8 items-center justify-center rounded-md border transition-colors disabled:opacity-30 ${danger ? "border-red-400/40 text-red-300 hover:bg-red-400/10" : "border-[#2a2a34] text-neutral-300 hover:border-amber-400/50"}`}>
      {children}
    </button>
  );
}
